import React, { useEffect, useMemo, useState } from 'react';
import { ADX, Stochastic } from 'technicalindicators';
import { useBarsFetcher } from '../../hooks/useTradeStationQueries';
import { MARKET_INDEX_CONFIG } from '../../constants/marketIndices';
import ThemedSelect from '../shared/ThemedSelect';
import StrengthOdometer from './StrengthOdometer';

export const MARKET_STRENGTH_SYMBOLS = ['SPY', 'QQQ', 'DIA', 'IWM'];
export const MARKET_STRENGTH_RSI_PERIOD = 14;
export const MARKET_STRENGTH_ADX_PERIOD = 14;
export const MARKET_STRENGTH_STOCH_PERIOD = 14;
export const MARKET_STRENGTH_STOCH_SIGNAL_PERIOD = 3;
/** Wilder's smoothing needs a long runway before ADX/RSI settle to platform values. */
const MARKET_STRENGTH_BARS_BACK = 300;
/**
 * ADX almost never clears 50 in practice, so plotting it raw would pin the needle to the
 * weak half forever. Doubling puts Wilder's 25 "trending" threshold on the arc midpoint.
 */
const ADX_GAUGE_SCALE = 2;

export function calculateRsi(closes, period = MARKET_STRENGTH_RSI_PERIOD) {
  const values = (closes || []).map(Number).filter(Number.isFinite);
  if (values.length <= period) return null;

  let averageGain = 0;
  let averageLoss = 0;
  for (let index = 1; index <= period; index += 1) {
    const change = values[index] - values[index - 1];
    averageGain += Math.max(change, 0);
    averageLoss += Math.max(-change, 0);
  }
  averageGain /= period;
  averageLoss /= period;

  for (let index = period + 1; index < values.length; index += 1) {
    const change = values[index] - values[index - 1];
    averageGain = ((averageGain * (period - 1)) + Math.max(change, 0)) / period;
    averageLoss = ((averageLoss * (period - 1)) + Math.max(-change, 0)) / period;
  }

  if (averageLoss === 0) return averageGain === 0 ? 50 : 100;
  if (averageGain === 0) return 0;
  return 100 - (100 / (1 + (averageGain / averageLoss)));
}

/**
 * Wilder's ADX — the standard trend-strength read, and the counterweight to RSI here:
 * RSI says which way momentum leans, ADX says whether the move has any conviction.
 */
export function calculateAdx(bars, period = MARKET_STRENGTH_ADX_PERIOD) {
  const rows = (bars || []).filter((bar) => (
    Number.isFinite(bar?.high) && Number.isFinite(bar?.low) && Number.isFinite(bar?.close)
  ));
  if (rows.length < period * 2) return null;

  const result = ADX.calculate({
    high: rows.map((bar) => bar.high),
    low: rows.map((bar) => bar.low),
    close: rows.map((bar) => bar.close),
    period,
  });
  const adx = result?.[result.length - 1]?.adx;
  return Number.isFinite(adx) ? adx : null;
}

/** Standard fast %K stochastic oscillator. %D remains smoothing context, not another needle. */
export function calculateStochastic(
  bars,
  period = MARKET_STRENGTH_STOCH_PERIOD,
  signalPeriod = MARKET_STRENGTH_STOCH_SIGNAL_PERIOD,
) {
  const rows = (bars || []).filter((bar) => (
    Number.isFinite(bar?.high) && Number.isFinite(bar?.low) && Number.isFinite(bar?.close)
  ));
  if (rows.length < period + signalPeriod - 1) return null;

  const result = Stochastic.calculate({
    high: rows.map((bar) => bar.high),
    low: rows.map((bar) => bar.low),
    close: rows.map((bar) => bar.close),
    period,
    signalPeriod,
  });
  const stochastic = result?.[result.length - 1]?.k;
  return Number.isFinite(stochastic) ? stochastic : null;
}

function strengthLabelForRsi(rsi) {
  if (!Number.isFinite(rsi)) return null;
  if (rsi >= 70) return 'Overbought';
  if (rsi >= 55) return 'Strong';
  if (rsi > 45) return 'Neutral';
  if (rsi > 30) return 'Weak';
  return 'Oversold';
}

function trendLabelForAdx(adx) {
  if (!Number.isFinite(adx)) return null;
  if (adx >= 50) return 'Strong trend';
  if (adx >= 25) return 'Trending';
  if (adx >= 20) return 'Weak trend';
  return 'No trend';
}

function strengthLabelForStochastic(stochastic) {
  if (!Number.isFinite(stochastic)) return null;
  if (stochastic >= 80) return 'Overbought';
  if (stochastic >= 60) return 'Strong';
  if (stochastic > 40) return 'Neutral';
  if (stochastic > 20) return 'Weak';
  return 'Oversold';
}

export default function MarketStrengthOdometer({ height = 176 }) {
  const fetchBars = useBarsFetcher();
  const [symbol, setSymbol] = useState('SPY');
  const [rsi, setRsi] = useState(null);
  const [adx, setAdx] = useState(null);
  const [stochastic, setStochastic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBars({
      ticker: symbol,
      interval: 1,
      unit: 'Daily',
      barsback: MARKET_STRENGTH_BARS_BACK,
      sessionTemplate: 'Default',
    })
      .then((data) => {
        if (cancelled) return;
        const bars = (data?.Bars || [])
          .map((bar, index) => ({
            high: parseFloat(bar.High),
            low: parseFloat(bar.Low),
            close: parseFloat(bar.Close),
            time: Date.parse(bar.TimeStamp),
            index,
          }))
          .filter((bar) => Number.isFinite(bar.close))
          .sort((a, b) => (
            Number.isFinite(a.time) && Number.isFinite(b.time)
              ? a.time - b.time
              : a.index - b.index
          ));
        setRsi(calculateRsi(bars.map((bar) => bar.close)));
        setAdx(calculateAdx(bars));
        setStochastic(calculateStochastic(bars));
      })
      .catch(() => {
        if (cancelled) return;
        setRsi(null);
        setAdx(null);
        setStochastic(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchBars, symbol]);

  const options = useMemo(
    () => MARKET_STRENGTH_SYMBOLS.map((value) => ({
      value,
      label: MARKET_INDEX_CONFIG[value]?.name || value,
    })),
    [],
  );

  const series = useMemo(() => ([
    {
      key: 'rsi',
      label: `RSI(${MARKET_STRENGTH_RSI_PERIOD})`,
      value: rsi,
      gaugeScore: rsi,
      strengthLabel: strengthLabelForRsi(rsi),
    },
    {
      key: 'adx',
      label: `ADX(${MARKET_STRENGTH_ADX_PERIOD})`,
      value: adx,
      gaugeScore: Number.isFinite(adx) ? Math.min(100, adx * ADX_GAUGE_SCALE) : null,
      strengthLabel: trendLabelForAdx(adx),
      dashed: true,
    },
    {
      key: 'stochastic',
      label: `Stoch(${MARKET_STRENGTH_STOCH_PERIOD},${MARKET_STRENGTH_STOCH_SIGNAL_PERIOD})`,
      value: stochastic,
      gaugeScore: stochastic,
      strengthLabel: strengthLabelForStochastic(stochastic),
    },
  ]), [adx, rsi, stochastic]);

  return (
    <StrengthOdometer
      score100={rsi}
      series={series}
      loading={loading}
      label={`RSI(${MARKET_STRENGTH_RSI_PERIOD})`}
      strengthLabel={strengthLabelForRsi(rsi)}
      height={height}
      variant="circular"
      headerControl={(
        <ThemedSelect
          value={symbol}
          options={options}
          onChange={setSymbol}
          aria-label="Market strength index"
          width={124}
          minWidth={112}
          height={30}
          fontSize="0.75rem"
          testId="market-strength-index"
        />
      )}
    />
  );
}
